import { useRef, useState } from "react";
import styled, { css } from "styled-components";
import { animated, config, useSpring } from "@react-spring/web";
import {
  Button,
  Card,
  Icon,
  Input,
  Layout,
  Modal,
  Popover,
  scrollListStyle,
  TextButton,
  Title,
  useTheme
} from "@introist/react-foundation/v2";

import { useMenuCollapsed } from "./context";
import { WorkspaceSelectorProps } from "./types";

// One selector for both places it appears: "full" (logo + name, in the side menu) and "logo"
// (logo only, at the bottom of the product rail). The dropdown is the same in both.

const StyledCard = styled(Card)`
  width: 18rem;
  max-height: 36rem;
  padding: var(--spacing-large);
  padding-top: var(--spacing-medium);
  ${scrollListStyle};

  * {
    user-select: none;
  }
`;

const MenuButton = styled(Button)`
  && {
    padding: var(--spacing-small);
    span {
      flex: initial;
    }
  }
`;

const LogoWrapper = styled.div<{ $clickable?: boolean }>`
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: var(--rounding-medium);
  overflow: hidden;
  ${({ $clickable }) => $clickable && "cursor: pointer;"}

  > img {
    width: 100%;
    object-fit: contain;
  }
`;

const FirstCharacter = styled(Title)`
  width: inherit;
  height: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background-color: var(--palette-primary-subdued);
`;

const WorkspaceButton = styled.div<{ $current: boolean }>`
  padding: var(--spacing-small);
  background-color: transparent;
  border: 1px solid var(--palette-border-subdued);
  border-radius: var(--rounding-medium);
  transition: border-color 160ms ease;
  cursor: pointer;

  :hover {
    border-color: var(--palette-primary-default);
  }

  ${({ $current }) =>
    $current &&
    css`
      border-color: var(--palette-primary-default);
    `}
`;

const BlueCheckIcon = styled(Icon).attrs({ name: "check" })`
  margin-left: auto;
  stroke: var(--palette-primary-default);
`;

const CreateWorkspaceTextButton = styled(TextButton)`
  padding-left: var(--spacing-small);
`;

const FullAnchorButton = styled.button<{ $active?: boolean }>`
  display: grid;
  align-items: center;
  grid-template-columns: 1.5rem auto;
  grid-column-gap: var(--spacing-small);
  width: 100%;
  padding: var(--spacing-small);
  border: 1px solid var(--palette-border-subdued);
  border-radius: var(--rounding-medium);
  background-color: transparent;
  transition: border-color 160ms ease;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    border-color: var(--palette-primary-default);
  }

  ${({ $active }) =>
    $active &&
    css`
      border-color: var(--palette-primary-default);
    `}
`;

const EllipsisWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: stretch;
`;

const EllipsisTitle = styled(Title)`
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AnimatedWrapper = styled(animated.div)`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-small);
`;

const firstCharacter = (input: string) => (input.length > 0 ? input[0].toUpperCase() : null);

const Logo = ({
  name,
  logo,
  clickable,
  onClick
}: {
  name: string;
  logo?: React.ReactNode;
  clickable?: boolean;
  onClick?: () => void;
}) => (
  <LogoWrapper $clickable={clickable} onClick={onClick}>
    {logo ? <>{logo}</> : <FirstCharacter variant="bold">{firstCharacter(name)}</FirstCharacter>}
  </LogoWrapper>
);

const FullAnchor = ({
  organisationName,
  logo,
  open,
  onClick,
  anchorRef
}: {
  organisationName: string;
  logo: React.ReactNode;
  open: boolean;
  onClick: () => void;
  anchorRef: React.MutableRefObject<HTMLElement | null>;
}) => {
  const { theme } = useTheme();
  const collapsed = useMenuCollapsed();

  const spring = useSpring({
    opacity: collapsed ? 0 : 1,
    transform: collapsed ? "translateX(0.5rem)" : "translateX(0rem)",
    config: { ...config.stiff, clamp: true }
  });

  return (
    <FullAnchorButton
      ref={el => (anchorRef.current = el)}
      onClick={onClick}
      $active={open}
    >
      <Logo name={organisationName} logo={logo} />
      <AnimatedWrapper style={spring}>
        <EllipsisWrapper>
          <EllipsisTitle variant="bold" style={{ maxWidth: 106 }}>
            {organisationName}
          </EllipsisTitle>
        </EllipsisWrapper>
        <Icon
          name="caretDown"
          color={theme.palette.foreground.subdued}
          style={{ marginLeft: "auto" }}
        />
      </AnimatedWrapper>
    </FullAnchorButton>
  );
};

export const WorkspaceSelector = ({
  variant,
  user,
  workspaces,
  organisation,
  createWorkspace,
  switchWorkspace,
  onLogout,
  accountItems = []
}: WorkspaceSelectorProps & { variant: "full" | "logo" }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLElement | null>(null);

  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");

  const toggle = () => setOpen(!open);

  return (
    <div>
      {variant === "full" ? (
        <FullAnchor
          organisationName={organisation.name}
          logo={organisation.logo}
          open={open}
          onClick={toggle}
          anchorRef={anchorRef}
        />
      ) : (
        <div ref={el => (anchorRef.current = el)}>
          <Logo name={organisation.name} logo={organisation.logo} clickable onClick={toggle} />
        </div>
      )}

      <Popover open={open} referenceElement={anchorRef.current} onClose={() => setOpen(false)}>
        <StyledCard elevated>
          <Layout.Group vertical gap="large">
            <EllipsisWrapper style={{ marginTop: 4 }}>
              <EllipsisTitle>{user.email}</EllipsisTitle>
            </EllipsisWrapper>
            <Layout.Divider />

            <Layout.Group vertical gap="small">
              {workspaces &&
                workspaces.length > 1 &&
                workspaces.map(workspace => {
                  const current = workspace.name === organisation.name;

                  return (
                    <WorkspaceButton
                      key={workspace.id}
                      $current={current}
                      onClick={() => switchWorkspace && switchWorkspace(workspace.id)}
                    >
                      <Layout.Group>
                        <LogoWrapper>
                          {workspace.logo ? (
                            <img src={workspace.logo} alt="" />
                          ) : (
                            <FirstCharacter variant="bold">
                              {firstCharacter(workspace.name)}
                            </FirstCharacter>
                          )}
                        </LogoWrapper>
                        <Title variant="bold">{workspace.name}</Title>
                        {current && <BlueCheckIcon />}
                      </Layout.Group>
                    </WorkspaceButton>
                  );
                })}
            </Layout.Group>
            {createWorkspace && (
              <CreateWorkspaceTextButton
                startIcon="plus"
                colorVariant="primary"
                onClick={() => setCreateWorkspaceOpen(true)}
              >
                Create workspace
              </CreateWorkspaceTextButton>
            )}
            <Layout.Divider />
            <Layout.Group vertical gap="xSmall">
              {accountItems.map(item => (
                <MenuButton
                  key={item.key}
                  variant="blended"
                  startAdornment={item.icon}
                  onClick={() => {
                    setOpen(false);
                    item.onClick();
                  }}
                >
                  {item.title}
                </MenuButton>
              ))}
              <MenuButton
                variant="blended"
                startAdornment={<Icon name="logOut" />}
                onClick={onLogout}
              >
                Sign out
              </MenuButton>
            </Layout.Group>
          </Layout.Group>
        </StyledCard>
      </Popover>

      {createWorkspace && (
        <Modal
          title="Create new workspace"
          open={createWorkspaceOpen}
          onClose={() => setCreateWorkspaceOpen(false)}
          style={{ width: "350px" }}
          primaryButton={{
            title: "Create",
            onClick: () => createWorkspace(newWorkspaceName)
          }}
          secondaryButton={{
            title: "Cancel",
            onClick: () => setCreateWorkspaceOpen(false)
          }}
        >
          <Input
            autoFocus
            placeholder="Workspace name"
            value={newWorkspaceName}
            onChange={setNewWorkspaceName}
          />
        </Modal>
      )}
    </div>
  );
};
